import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SessionTimeout = 15 | 30 | 60 | 120; // minutes

export interface SidebarItemConfig {
  id: string;
  visible: boolean;
}

interface SettingsState {
  sessionTimeout: SessionTimeout; // minutes
  emailNotifications: boolean;
  lowStockAlerts: boolean;
  avatarUrl: string | null;
  beaconPersonId: number | null;
  avatarDismissed: boolean; // User dismissed the avatar prompt

  // Sidebar customisation - ordered arrays of { id, visible }
  // null means "use defaults" (before user has customised)
  sidebarNavOrder: SidebarItemConfig[] | null;
  sidebarExternalOrder: SidebarItemConfig[] | null;

  // Actions
  setSessionTimeout: (timeout: SessionTimeout) => void;
  setEmailNotifications: (enabled: boolean) => void;
  setLowStockAlerts: (enabled: boolean) => void;
  setAvatar: (url: string | null, personId: number | null) => void;
  dismissAvatarPrompt: () => void;
  setSidebarNavOrder: (order: SidebarItemConfig[]) => void;
  setSidebarExternalOrder: (order: SidebarItemConfig[]) => void;
  resetSidebarConfig: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      sessionTimeout: 30, // Default 30 minutes
      emailNotifications: true,
      lowStockAlerts: true,
      avatarUrl: null,
      beaconPersonId: null,
      avatarDismissed: false,
      sidebarNavOrder: null,
      sidebarExternalOrder: null,

      setSessionTimeout: (timeout) => set({ sessionTimeout: timeout }),
      setEmailNotifications: (enabled) => set({ emailNotifications: enabled }),
      setLowStockAlerts: (enabled) => set({ lowStockAlerts: enabled }),
      setAvatar: (url, personId) => set({ avatarUrl: url, beaconPersonId: personId }),
      dismissAvatarPrompt: () => set({ avatarDismissed: true }),
      setSidebarNavOrder: (order) => set({ sidebarNavOrder: order }),
      setSidebarExternalOrder: (order) => set({ sidebarExternalOrder: order }),
      resetSidebarConfig: () => set({ sidebarNavOrder: null, sidebarExternalOrder: null }),
    }),
    {
      name: 'sca-uk-settings', // localStorage key
    }
  )
);
